import { useLazyQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { StaffSearchTable } from './staff-search-table.tsx';
import { ComponentQueryLoader } from '../../../shared/component-query-loader.tsx';
import { useSearchState } from '../../../shared/use-search-state';
import { StaffStaffSearchContainerStaffUsersDocument, StaffStaffSearchContainerStaffUsersSearchFieldsFragment } from '../../../../generated.tsx';
import { useSearchParams } from 'react-router-dom';
import { theme, Form, Input, Button } from 'antd';

interface StaffSearchContainerProps {
  setFilterDataCount: React.Dispatch<React.SetStateAction<StaffStaffSearchContainerStaffUsersSearchFieldsFragment>>;
  isMobile: boolean;
  refreshQuery: boolean;
  setRefreshQuery: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StaffSearchContainer: React.FC<StaffSearchContainerProps> = (props) => {
  const [searchParams] = useSearchParams();

  const { inputValue, setInputValue, handleSearchSubmit } = useSearchState();

  const {
    token: { colorBgContainer }
  } = theme.useToken();

  const getEverythingFromUrl = (includeParams: string[] = []) => {
    const filters: any = {};
    for (let [key, value] of searchParams.entries()) {
      if (!includeParams.includes(key)) {
        continue;
      }
      if (key === 'accessBlocked') {
        filters[key] = value === 'true';
      } else {
        filters[key] = value.split(',');
      }
    }
    return filters;
  };

  const getSingleValueFromUrl = (key: string) => {
    return searchParams.get(key);
  };

  const [doSearch, { data: searchResult, loading: searchResultLoading, error: searchResultError, refetch: refetchSearchResult }] = useLazyQuery(
    StaffStaffSearchContainerStaffUsersDocument,
    {
      fetchPolicy: 'network-only'
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onSearchStringClear = () => {
    setInputValue('');
    handleSearchSubmit('');
  };

  useEffect(() => {
    if (searchResult) {
      props?.setFilterDataCount(searchResult?.staffUserSearch?.facets as StaffStaffSearchContainerStaffUsersSearchFieldsFragment);
    }
  }, [searchResult, props?.setFilterDataCount]);

  useEffect(() => {
    if (props.refreshQuery) {
      refetchSearchResult();
      props.setRefreshQuery(false);
    }
  }, [props.refreshQuery]);

  useEffect(() => {
    doSearch({
      variables: {
        input: {
          options: {
            filter: getEverythingFromUrl(['updatedAt', 'createdAt', 'schemaVersion', 'role', 'tags', 'accessBlocked']),
            skip: getSingleValueFromUrl('page') ? (parseInt(getSingleValueFromUrl('page') ?? '1', 10) - 1) * parseInt(getSingleValueFromUrl('pageSize') ?? '10', 10) : 0,
            top: parseInt(getSingleValueFromUrl('pageSize') ?? '10', 10)
          },
          searchString: getSingleValueFromUrl('search')
        }
      }
    });
  }, [searchParams]);

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setInputValue(search);
  }, []);

  return (
    <div
      className={`
      ${props.isMobile ? 'pl-0' : 'pl-12'} 
      bg-[${colorBgContainer}] min-w-[300px] w-full p-12 pt-0 rounded-lg flex-[1_1_30%]
    `}
    >
      <Form
        layout="inline"
        onFinish={() => {
          handleSearchSubmit(inputValue);
        }}
        className="mb-1"
      >
        <Form.Item>
          <Input
            maxLength={200}
            value={inputValue}
            allowClear
            onClear={onSearchStringClear}
            onChange={handleInputChange}
            className="min-w-20 sm:min-w-80 border border-black mb-2"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" className="ml-0 mr-5">
            Search
          </Button>
        </Form.Item>
      </Form>
      <ComponentQueryLoader
        loading={searchResultLoading}
        hasData={searchResult}
        error={searchResultError}
        hasDataComponent={
          <StaffSearchTable
            resultCount={searchResult?.staffUserSearch?.count || 0}
            data={searchResult?.staffUserSearch?.staffUserResults as StaffStaffSearchContainerStaffUsersSearchFieldsFragment[]}
          />
        }
      />
    </div>
  );
};
